import { instanceBo } from "../utils/axios.create";

export interface EnrollmentSummaryFilters {
	accountId: string;
	programId: string;
	orgUnitId?: string;
	researcherId?: string;
	startDate?: string;
	endDate?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface EnrollmentSummaryKpis {
	totalEnrolled: number;
	active: number;
	completed: number;
	disenrolled: number;
	avgCompletionDays: number;
}

export interface EnrollmentSummaryByOrgUnit {
	orgUnitId: string;
	orgUnit: string;
	active: number;
	completed: number;
	disenrolled: number;
	paused: number;
}

export interface EnrollmentSummaryRow {
	enrollmentId: string;
	participantName: string;
	// SIMS-8895: rendered instead of the name when the account has
	// displayBeneficiaryName off. Empty when the participant has no CIN.
	participantCin: string;
	orgUnit: string;
	researcher: string;
	startDate: string | null;
	day: number;
	totalDays: number;
	status: "ACTIVE" | "COMPLETED" | "DISENROLLED" | "PAUSED";
	completionPct: number;
}

export interface EnrollmentSummaryFilterOption {
	id: string;
	name: string;
}

export interface EnrollmentSummaryPagination {
	pageNumber: number;
	pageSize: number;
	total: number;
}

export interface EnrollmentSummaryResponse {
	kpis: EnrollmentSummaryKpis;
	byOrgUnit: EnrollmentSummaryByOrgUnit[];
	enrollments: EnrollmentSummaryRow[];
	availableOrgUnits: EnrollmentSummaryFilterOption[];
	availableResearchers: EnrollmentSummaryFilterOption[];
	pagination: EnrollmentSummaryPagination;
}

const getEnrollmentSummary = async (
	filters: EnrollmentSummaryFilters,
): Promise<EnrollmentSummaryResponse> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.orgUnitId) params.orgUnitId = filters.orgUnitId;
	if (filters.researcherId) params.researcherId = filters.researcherId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/enrollment-summary", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

export interface IncentiveDisbursementFilters {
	accountId: string;
	programId: string;
	orgUnitId?: string;
	researcherId?: string;
	startDate?: string;
	endDate?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface IncentiveDisbursementKpis {
	totalEarned: number;
	totalReceived: number;
	avgEarned: number;
	potential: number;
	engagementRate: number;
}

export interface IncentiveDisbursementByDay {
	day: number;
	earned: number;
	potential: number;
}

export interface IncentiveDisbursementByEnrollment {
	enrollmentId: string;
	participantName: string;
	// SIMS-8895: rendered instead of the name when the account has
	// displayBeneficiaryName off. Empty when the participant has no CIN.
	participantCin: string;
	orgUnit: string;
	researcher: string;
	potential: number;
	earned: number;
	received: number;
	engagementRate: number;
}

export interface IncentiveDisbursementResponse {
	kpis: IncentiveDisbursementKpis;
	byDay: IncentiveDisbursementByDay[];
	byEnrollment: IncentiveDisbursementByEnrollment[];
	availableOrgUnits: EnrollmentSummaryFilterOption[];
	availableResearchers: EnrollmentSummaryFilterOption[];
	pagination: EnrollmentSummaryPagination;
}

const getIncentiveDisbursement = async (
	filters: IncentiveDisbursementFilters,
): Promise<IncentiveDisbursementResponse> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.orgUnitId) params.orgUnitId = filters.orgUnitId;
	if (filters.researcherId) params.researcherId = filters.researcherId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/incentive-disbursement", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

export interface EducationalEngagementFilters {
	accountId: string;
	programId: string;
	// SIMS-9052 (AC1): the consolidated export states an org unit and a
	// researcher on its single Cover sheet, so every sub-report in that workbook
	// has to honour them. The tab itself hides both controls, so in practice
	// only the export sends these.
	orgUnitId?: string;
	researcherId?: string;
	startDate?: string;
	endDate?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface EducationalEngagementKpis {
	contentItems: number;
	totalAccesses: number;
	avgAccessRate: number;
}

export interface EducationalEngagementByItem {
	itemId: string;
	title: string;
	type: string;
	accesses: number;
	accessRate: number;
}

export interface EducationalEngagementItemFlag {
	itemId: string;
	accessed: boolean;
}

export interface EducationalEngagementByEnrollment {
	enrollmentId: string;
	participantName: string;
	// SIMS-8895: rendered instead of the name when the account has
	// displayBeneficiaryName off. Empty when the participant has no CIN.
	participantCin: string;
	items: EducationalEngagementItemFlag[];
}

export interface EducationalEngagementResponse {
	kpis: EducationalEngagementKpis;
	byItem: EducationalEngagementByItem[];
	byEnrollment: EducationalEngagementByEnrollment[];
	pagination: EnrollmentSummaryPagination;
}

const getEducationalEngagement = async (
	filters: EducationalEngagementFilters,
): Promise<EducationalEngagementResponse> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.orgUnitId) params.orgUnitId = filters.orgUnitId;
	if (filters.researcherId) params.researcherId = filters.researcherId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/educational-engagement", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

export interface EventActivityFilters {
	accountId: string;
	programId: string;
	// SIMS-9052 (AC1): the consolidated export states an org unit and a
	// researcher on its single Cover sheet, so every sub-report in that workbook
	// has to honour them. The tab itself hides both controls, so in practice
	// only the export sends these.
	orgUnitId?: string;
	researcherId?: string;
	startDate?: string;
	endDate?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface EventActivityEvent {
	eventId: string;
	name: string;
}

export interface EventActivityKpis {
	expected: number;
	submitted: number;
	missed: number;
	pending: number;
}

export type EventActivityStatus = "SUBMITTED" | "MISSED" | "PENDING";

export interface EventActivitySubmission {
	eventId: string;
	status: EventActivityStatus;
}

export interface EventActivityDay {
	day: number;
	submissions: EventActivitySubmission[];
}

export interface EventActivityRow {
	enrollmentId: string;
	participantName: string;
	// SIMS-8895: rendered instead of the name when the account has
	// displayBeneficiaryName off. Empty when the participant has no CIN.
	participantCin: string;
	orgUnit: string;
	completionPct: number;
	days: EventActivityDay[];
}

export interface EventActivityResponse {
	events: EventActivityEvent[];
	kpis: EventActivityKpis;
	rows: EventActivityRow[];
	pagination: EnrollmentSummaryPagination;
}

const getEventActivity = async (
	filters: EventActivityFilters,
): Promise<EventActivityResponse> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.orgUnitId) params.orgUnitId = filters.orgUnitId;
	if (filters.researcherId) params.researcherId = filters.researcherId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/event-activity", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

export interface EventRatingsFilters {
	accountId: string;
	programId: string;
	// SIMS-9052 (AC1): the consolidated export states an org unit and a
	// researcher on its single Cover sheet, so every sub-report in that workbook
	// has to honour them. The tab itself hides both controls, so in practice
	// only the export sends these.
	orgUnitId?: string;
	researcherId?: string;
	eventId?: string;
	enrollmentId?: string;
	startDate?: string;
	endDate?: string;
}

export type EventRatingType = "SCORED_SUM" | "INDEPENDENT_RATINGS";

export interface EventRatingsEvent {
	eventId: string;
	name: string;
	ratingType: EventRatingType;
}

// SIMS-5825: one program day for one enrollment. Which fields carry a value
// depends on the event's rating type — `score` for SCORED_SUM, `emoji` and
// `distress` for INDEPENDENT_RATINGS. A day the participant did not submit is
// null on every field, which is distinct from a zero.
export interface EventRatingsEnrollmentDay {
	day: number;
	score?: number | null;
	emoji?: string | null;
	distress?: number | null;
}

/**
 * SIMS-9147: one scheduled occurrence of the selected event. A workflow can
 * schedule the same event several times a day (SOWS at +30/+60/+90); each one is
 * its own entry, oldest first by plannedFor. One never submitted is still listed,
 * with null values, so the export can keep it in its column.
 */
export interface EventRatingsEnrollmentEvent {
	day: number;
	plannedFor?: string | null;
	workflowActionId?: string | null;
	score?: number | null;
	emoji?: string | null;
	distress?: number | null;
}
export interface EventRatingsEnrollment {
	enrollmentId: string;
	participantName: string;
	// SIMS-8895: rendered instead of the name when the account has
	// displayBeneficiaryName off. Empty when the participant has no CIN.
	participantCin: string;
	// SIMS-5825: always 1..14 in order once an event is selected, so the export
	// can lay out columns without checking for gaps. Empty before one is chosen —
	// the values are per event and this list is returned either way.
	byDay: EventRatingsEnrollmentDay[];
	// SIMS-9147: every occurrence, which the export lays out one column each.
	// Optional: a backend without it falls back to byDay, one per day.
	byEvent?: EventRatingsEnrollmentEvent[];
	// SCORED_SUM only, over every submitted reading.
	avgScore?: number | null;
	minScore?: number | null;
	maxScore?: number | null;
}

export interface ScoredSumDay {
	day: number;
	avgScore: number | null;
	submissionCount: number;
}

export interface ScoredSumKpis {
	avgScoreDay1: number | null;
	avgScoreDay14: number | null;
	improvementPct: number | null;
	submissionRate: number;
}

export interface ScoredSumPayload {
	scoreMin: number;
	scoreMax: number;
	avgScoreByDay: ScoredSumDay[];
	kpis: ScoredSumKpis;
}

export interface IndependentDistressDay {
	day: number;
	avgDistress: number | null;
	submissionCount: number;
}

export interface IndependentEmojiDay {
	day: number;
	counts: Record<string, number>;
}

export interface IndependentEmojiPick {
	value: string;
	count: number;
}

export interface IndependentKpis {
	avgDistressDay1: number | null;
	avgDistressDay14: number | null;
	mostCommonEmojiDay1: IndependentEmojiPick | null;
	submissionRate: number;
}

export interface IndependentRatingsPayload {
	distressField: { name: string; min: number; max: number };
	emojiField: { name: string; values: string[] };
	avgDistressByDay: IndependentDistressDay[];
	emojiCountsByDay: IndependentEmojiDay[];
	kpis: IndependentKpis;
}

export interface EventRatingsChart {
	eventId: string;
	name: string;
	ratingType: EventRatingType;
	scoredSum?: ScoredSumPayload;
	independent?: IndependentRatingsPayload;
}

export interface EventRatingsResponse {
	events: EventRatingsEvent[];
	enrollments: EventRatingsEnrollment[];
	chart: EventRatingsChart | null;
}

const getEventRatings = async (
	filters: EventRatingsFilters,
): Promise<EventRatingsResponse> => {
	const params: Record<string, string> = {
		programId: filters.programId,
	};
	if (filters.orgUnitId) params.orgUnitId = filters.orgUnitId;
	if (filters.researcherId) params.researcherId = filters.researcherId;
	if (filters.eventId) params.eventId = filters.eventId;
	if (filters.enrollmentId) params.enrollmentId = filters.enrollmentId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;

	return instanceBo
		.get("/reports/event-ratings", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

// ---------------------------------------------------------------------------
// SIMS-6960 — Enrollment Summary (configurable events). Native (no-Qlik)
// report: one row per enrollment with lifetime, event-config-driven result
// columns. Backend-paginated and scoped by accountId + isDeleted.
// ---------------------------------------------------------------------------

export interface EnrollmentSummaryConfigurableFilters {
	accountId: string;
	programId: string;
	status?: string;
	// SIMS-7595: comma-separated OU ids; multi-select like the other reports.
	orgUnitIds?: string;
	providerId?: string;
	filterSummary?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface ResultColumn {
	key: string;
	label: string;
}

export interface ReportFilterOption {
	id: string;
	name: string | null;
}

export interface EnrollmentSummaryConfigurableRow {
	enrollmentId: string;
	beneficiaryName: string | null;
	cin: string | null;
	site: string | null;
	provider: string | null;
	npi: string | null;
	startDate: string | null;
	endDate: string | null;
	status: string;
	payerPlanName: string | null;
	payerPlanNumber: string | null;
	resultCounts: Record<string, number>;
	incentiveEarned: number;
	incentiveDisbursed: number;
	bankBalance: number;
}

export interface EnrollmentSummaryConfigurableResponse {
	columns: ResultColumn[];
	rows: EnrollmentSummaryConfigurableRow[];
	availableSites: ReportFilterOption[];
	availableProviders: ReportFilterOption[];
	pagination: EnrollmentSummaryPagination;
	/** SIMS-8014 / FR-6 — optional for the same rollout reason as the other reports. */
	programFilters?: ProgramEffectiveFilters[];
}

const getEnrollmentSummaryConfigurable = async (
	filters: EnrollmentSummaryConfigurableFilters,
): Promise<EnrollmentSummaryConfigurableResponse> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.status) params.status = filters.status;
	if (filters.orgUnitIds) params.orgUnitIds = filters.orgUnitIds;
	if (filters.providerId) params.providerId = filters.providerId;
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/enrollment-summary-configurable", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

// Backend-generated Excel (exceljs) for the configurable Enrollment Summary.
const getEnrollmentSummaryConfigurableExcel = async (
	filters: EnrollmentSummaryConfigurableFilters,
): Promise<Blob> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.status) params.status = filters.status;
	if (filters.orgUnitIds) params.orgUnitIds = filters.orgUnitIds;
	if (filters.providerId) params.providerId = filters.providerId;
	if (filters.filterSummary) params.filterSummary = filters.filterSummary;

	return instanceBo
		.get("/reports/enrollment-summary-configurable/export", {
			params,
			responseType: "blob",
			// SIMS-7588: force a single, specific Accept that matches the API
			// Gateway BinaryMediaTypes list. Axios' default multi-value Accept
			// ("application/json, text/plain, */*") does not match cleanly, so
			// API Gateway returns the base64 body undecoded and the .xlsx is
			// corrupt. This header makes API Gateway decode base64 → binary.
			headers: {
				Accept:
					"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			},
		})
		.then((res) => res.data as Blob)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

// Fetch every filtered row (paged, capped) for the client-side PDF export.
const fetchAllEnrollmentSummaryConfigurable = async (
	filters: EnrollmentSummaryConfigurableFilters,
): Promise<{
	columns: ResultColumn[];
	rows: EnrollmentSummaryConfigurableRow[];
}> => {
	const PAGE_SIZE = 100;
	const EXPORT_CAP = 5000;
	let pageNumber = 0;
	let columns: ResultColumn[] = [];
	const rows: EnrollmentSummaryConfigurableRow[] = [];
	for (;;) {
		const res = await getEnrollmentSummaryConfigurable({
			...filters,
			pageNumber,
			pageSize: PAGE_SIZE,
		});
		if (pageNumber === 0) columns = res.columns;
		rows.push(...res.rows);
		pageNumber += 1;
		if (
			res.rows.length === 0 ||
			rows.length >= res.pagination.total ||
			rows.length >= EXPORT_CAP
		) {
			break;
		}
	}
	return { columns, rows };
};

// ---------------------------------------------------------------------------
// SIMS-5596 — Program Activity Summary (KPI report).
// ---------------------------------------------------------------------------

export interface ProgramActivitySummaryFilters {
	accountId: string;
	programId: string;
	orgUnitIds?: string;
	payerIds?: string;
	groupLabelIds?: string;
	providerId?: string;
	startDate?: string;
	endDate?: string;
}

export interface VisitResultBucket {
	key: string;
	label: string;
	count: number;
}

export interface VisitResultsByEvent {
	eventId: string;
	eventName: string | null;
	buckets: VisitResultBucket[];
}

/**
 * What the server actually applied to one selected program (SIMS-8014 / FR-6).
 *
 * Projected from the same per-(program, org-unit) scopes the backend built its
 * `$match` from, so the context line is an account of the real query rather than a
 * client-side re-derivation from `userInfo.programs` — which is the whole point of
 * FR-6, and the difference between a truthful line and a plausible one.
 */
export interface ProgramEffectiveFilters {
	programId: string;
	/** Org units actually applied to this program. */
	orgUnitIds: string[];
	/** True when no org-unit narrowing applies, so the whole program is in scope. */
	unrestricted: boolean;
	/** Requested org units that did NOT narrow this program. */
	notApplied: string[];
	/** Count for the per-program "N org units" sub-header. */
	orgUnitCount: number;
	/** True when narrowing left nothing, so this program contributes no rows. */
	excluded: boolean;
}

export interface EnrollmentCounts {
	enrolled: number;
	active: number;
	completed: number;
	disenrolled: number;
}

export interface IncentiveSummary {
	totalEarned: number;
	earnedTxnCount: number;
	totalDisbursed: number;
	disbursedTxnCount: number;
}

/**
 * SIMS-8038 (FR-7) — one selected program's slice of the three KPI payloads.
 *
 * Carries `programId` only: the display name is resolved client-side (the picker
 * already has it), which is also what AC2's alphabetical ordering sorts on.
 *
 * `active` still counts ENROLLMENTS — SIMS-8048 changes it to distinct people.
 */
export interface ProgramBreakdown {
	programId: string;
	enrollmentCounts: EnrollmentCounts;
	incentiveSummary: IncentiveSummary;
	/** Empty when this program has no visit results — the entry itself remains. */
	visitResultsByEvent: VisitResultsByEvent[];
}

export interface ProgramActivitySummaryResponse {
	labels: { singular: string; plural: string };
	/** Union across every selected program — what SIMS-8041's total band renders. */
	enrollmentCounts: EnrollmentCounts;
	incentiveSummary: IncentiveSummary;
	visitResultsByEvent: VisitResultsByEvent[];
	availableSites: ReportFilterOption[];
	availableProviders: ReportFilterOption[];
	/**
	 * OPTIONAL on purpose (SIMS-8014). The workspace convention ships the backend
	 * before the frontend, but during a rollout — or against an environment on the
	 * previous API version — this key is absent. Callers must fall back rather than
	 * render an empty context line or a zero hidden-count.
	 */
	programFilters?: ProgramEffectiveFilters[];
	/**
	 * OPTIONAL for the same reason as `programFilters` (SIMS-8038): the backend
	 * ships first, so against the previous API version this key is absent. When it
	 * is, the screen falls back to the single combined layout rather than rendering
	 * zero sections — a missing key must not read as "no programs contributed".
	 */
	programBreakdown?: ProgramBreakdown[];
}

const getProgramActivitySummary = async (
	filters: ProgramActivitySummaryFilters,
): Promise<ProgramActivitySummaryResponse> => {
	const params: Record<string, string> = { programId: filters.programId };
	if (filters.orgUnitIds) params.orgUnitIds = filters.orgUnitIds;
	if (filters.payerIds) params.payerIds = filters.payerIds;
	if (filters.groupLabelIds) params.groupLabelIds = filters.groupLabelIds;
	if (filters.providerId) params.providerId = filters.providerId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;

	return instanceBo
		.get("/reports/program-activity-summary", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

// ---------------------------------------------------------------------------
// SIMS-5663 — Visit Detail (one row per visit) + lazy analyte drill-down.
// ---------------------------------------------------------------------------

export interface VisitDetailFilters {
	accountId: string;
	programId: string;
	orgUnitIds?: string;
	payerIds?: string;
	groupLabelIds?: string;
	providerId?: string;
	startDate?: string;
	endDate?: string;
	cin?: string;
	filterSummary?: string;
	pageNumber?: number;
	pageSize?: number;
}

export interface VisitDetailRow {
	enrollmentId: string;
	udtResultId: string;
	// SIMS-8078 (AC1): the program this visit belongs to, so the merged
	// multi-program table can tag every row. Optional so a frontend deployed
	// ahead of the backend half degrades to an untagged row instead of throwing —
	// the same rollout guard `totalEarned` and `programFilters` use below.
	programId?: string | null;
	/**
	 * SIMS-8078 (AC1/AC2): the program's FULL display name — what the Program
	 * column renders, and the backend's first tie-break key when visits share a
	 * date. Not abbreviated: the Hi-Fi shortens names in the table but states no
	 * truncation rule, so the column ellipsizes instead of the API inventing one.
	 */
	programName?: string | null;
	/**
	 * SIMS-8081 (AC1): the enrollment's CURRENT status — an `EnrollmentStatus`
	 * enum value (`ACTIVE` / `COMPLETED` / `DISENROLLED` / `PAUSED`), never the
	 * status as of the visit date (out of scope per the BRD).
	 *
	 * Note the domain has FOUR values while the Hi-Fi draws only three — it has no
	 * treatment for `PAUSED`. Rendering must therefore handle an unlisted value
	 * safely rather than assume the three it saw. Optional for the same rollout
	 * reason as `programName`.
	 */
	enrollmentStatus?: string | null;
	visitDate: string | null;
	visitNumber: number | null;
	member: string | null;
	cin: string | null;
	orgUnit: string | null;
	provider: string | null;
	eventId: string | null;
	eventName: string | null;
	result: string | null;
	// SIMS-7984: `result` is a presentational label (the event's shortName, or the
	// display) and must never be used as a semantic key — SIMS-7770 changed it and
	// silently broke the chip colours. `resultId` is the stable EventResult id and
	// `resultColor` the admin-configured chip colour. Both are null for tampered
	// ("Invalid") and flag-driven ("Missed") rows, which have no matched result.
	resultId: string | null;
	resultColor: string | null;
	earned: number;
	payerPlanName: string | null;
	payerPlanNumber: string | null;
	hasAnalyteDetails: boolean;
}

export interface VisitDetailResponse {
	rows: VisitDetailRow[];
	// SIMS-7770 (B1): server-side sum of earned across the WHOLE filtered set
	// (all pages) — the Totals row uses this instead of a per-page reduce.
	// Optional so a frontend deployed ahead of the backend half degrades to 0.
	totalEarned?: number;
	availableSites: ReportFilterOption[];
	availableProviders: ReportFilterOption[];
	pagination: EnrollmentSummaryPagination;
	/** SIMS-8014 / FR-6 — optional for the same rollout reason as `totalEarned`. */
	programFilters?: ProgramEffectiveFilters[];
}

export interface AnalyteResult {
	shortName: string | null;
	scientificName: string | null;
	positive: boolean;
}

export interface VisitAnalyteResponse {
	cupName: string | null;
	isTampered: boolean;
	tamperedReasons: string[];
	analytes: AnalyteResult[];
}

const buildVisitDetailParams = (
	filters: VisitDetailFilters,
): Record<string, string | number> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.orgUnitIds) params.orgUnitIds = filters.orgUnitIds;
	if (filters.payerIds) params.payerIds = filters.payerIds;
	if (filters.groupLabelIds) params.groupLabelIds = filters.groupLabelIds;
	if (filters.providerId) params.providerId = filters.providerId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;
	if (filters.cin) params.cin = filters.cin;
	if (filters.filterSummary) params.filterSummary = filters.filterSummary;
	return params;
};

const getVisitDetail = async (
	filters: VisitDetailFilters,
): Promise<VisitDetailResponse> => {
	const params = buildVisitDetailParams(filters);
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;

	return instanceBo
		.get("/reports/visit-detail", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

const getVisitAnalytes = async (
	accountId: string,
	enrollmentId: string,
	udtResultId: string,
): Promise<VisitAnalyteResponse> =>
	instanceBo
		.get(`/reports/visit-detail/${enrollmentId}/${udtResultId}/analytes`)
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));

// Backend-generated Excel (exceljs) — one row per visit-analyte.
const getVisitDetailExcel = async (
	filters: VisitDetailFilters,
): Promise<Blob> =>
	instanceBo
		.get("/reports/visit-detail/export", {
			params: buildVisitDetailParams(filters),
			responseType: "blob",
			// SIMS-7588: force a single, specific Accept that matches the API
			// Gateway BinaryMediaTypes list, so API Gateway decodes base64 →
			// binary. See getEnrollmentSummaryConfigurableExcel for details.
			headers: {
				Accept:
					"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			},
		})
		.then((res) => res.data as Blob)
		.catch((err) => Promise.reject(err.response?.data ?? err));

// Fetch every filtered visit row (paged, capped) for the client-side PDF.
const fetchAllVisitDetail = async (
	filters: VisitDetailFilters,
): Promise<VisitDetailRow[]> => {
	const PAGE_SIZE = 100;
	const EXPORT_CAP = 5000;
	let pageNumber = 0;
	const rows: VisitDetailRow[] = [];
	for (;;) {
		const res = await getVisitDetail({
			...filters,
			pageNumber,
			pageSize: PAGE_SIZE,
		});
		rows.push(...res.rows);
		pageNumber += 1;
		if (
			res.rows.length === 0 ||
			rows.length >= res.pagination.total ||
			rows.length >= EXPORT_CAP
		) {
			break;
		}
	}
	return rows;
};

// ---------------------------------------------------------------------------
// SIMS-8120 — Disbursed Incentives (one row per disbursement).
// ---------------------------------------------------------------------------

export interface DisbursedIncentivesFilters {
	programId: string;
	orgUnitIds?: string;
	groupLabelIds?: string;
	providerId?: string;
	startDate?: string;
	endDate?: string;
	cin?: string;
	pageNumber?: number;
	pageSize?: number;
	/** AC5: transaction date is the only sortable column, so this is a direction. */
	sortDir?: "asc" | "desc";
}

export interface DisbursedIncentiveRow {
	/**
	 * SIMS-8131: the key the visits drill-down is fetched by.
	 *
	 * A payout cannot be identified by (date, cin, amount): two same-day
	 * disbursements of the same amount to one participant are legitimate, and
	 * expanding one would show the other's visits.
	 */
	transactionId: string;
	programName: string;
	transactionDate: string;
	cin: string;
	/** An em dash when the account withholds names — never the CIN (AC1). */
	beneficiaryName: string;
	vendorCard: string;
	delivery: string;
	amount: number;
}

export interface DisbursedIncentivesResponse {
	rows: DisbursedIncentiveRow[];
	/** Across the WHOLE filtered set, never the page (AC2). */
	totalDisbursed: number;
	pagination: { pageNumber: number; pageSize: number; total: number };
	/**
	 * AC6. `beneficiaryHeading` arrives ready to render so the screen and the
	 * SIMS-8155 export cannot derive a different heading from the same term.
	 */
	labels: { singular: string; plural: string; beneficiaryHeading: string };
	/** AC6 — the account's symbol, resolved server-side rather than assumed here. */
	currencySymbol: string;
	/**
	 * The direction the server actually applied. The header's arrow and aria-sort
	 * render from this rather than from what we asked for, so what is drawn is
	 * always what was applied.
	 */
	sortDir: "asc" | "desc";
	/**
	 * Filter options — SIMS-8123. Program-scoped and NOT narrowed by the current
	 * row filters, so an option does not vanish the moment the user picks it.
	 */
	availableSites: ReportFilterOption[];
	availableProviders: ReportFilterOption[];
}

/** One visit inside a disbursement's drill-down — SIMS-8131 AC1. */
export interface DisbursementVisit {
	visitDate: string;
	/** `null` renders an empty cell, never a misleading `0`. */
	visitNumber: number | null;
	/** The configured event name — the only identifier a non-visit activity has. */
	activity: string;
	cmCoordinator: string;
	earnedAmount: number;
	result: string;
	/**
	 * SIMS-9012: the admin-configured chip colour (`EventResult.displayColor`),
	 * or null when the result configures none — the chip then falls back to the
	 * theme tokens. The same field Visit Detail carries, so the two screens
	 * render the identical pill.
	 */
	resultColor: string | null;
	/** Resolved as of the visit date, so two visits can differ. */
	payerPlan: string | null;
	/**
	 * SIMS-9294: the member ID on the same plan period — Visit Detail's
	 * "Payer Plan ID". Null when no plan covers the date or none is stored.
	 */
	payerPlanNumber: string | null;
}

export interface DisbursementVisitsResponse {
	/** Empty is AC2's "no linked visits" case — a real state, not an error. */
	visits: DisbursementVisit[];
	earnedTotal: number;
	/**
	 * The DISBURSED amount, which is what the sub-footer shows (PRD DR-8) — not
	 * `earnedTotal`. They differ exactly on the cases SIMS-8138 exists for.
	 */
	disbursedAmount: number;
	/** AC3 — the same symbol the row above rendered. */
	currencySymbol: string;
	labels: { singular: string; plural: string; beneficiaryHeading: string };
}

/**
 * The visits behind ONE disbursement — SIMS-8131.
 *
 * `programId`/`orgUnitIds` travel with the transaction id because the server
 * RE-SCOPES the panel on this request; they are not redundant.
 */
const getDisbursementVisits = async (filters: {
	programId: string;
	transactionId: string;
	orgUnitIds?: string;
}): Promise<DisbursementVisitsResponse> => {
	const params: Record<string, string> = {
		programId: filters.programId,
		transactionId: filters.transactionId,
	};
	if (filters.orgUnitIds) params.orgUnitIds = filters.orgUnitIds;

	return instanceBo
		.get("/reports/disbursed-incentives/visits", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

/**
 * The visits behind MANY disbursements, for the SIMS-8178 export.
 *
 * Keyed by transaction id so it joins straight onto the export rows the caller
 * already holds. A payout the caller cannot see is absent; a visible payout
 * with no linked visits is present with an empty `visits` array — a real case
 * that SIMS-8138 turns into a full-amount residual.
 */
export interface DisbursementVisitsBulkResponse {
	byTransaction: Record<
		string,
		{
			visits: DisbursementVisit[];
			earnedTotal: number;
			disbursedAmount: number;
		}
	>;
	currencySymbol: string;
	labels: { singular: string; plural: string; beneficiaryHeading: string };
}

/** Ids per request. A URL cannot carry 5000 ObjectIds; the caller chunks. */
const VISITS_BULK_CHUNK = 100;

const getDisbursementVisitsBulk = async (filters: {
	programId: string;
	transactionIds: string;
	orgUnitIds?: string;
}): Promise<DisbursementVisitsBulkResponse> => {
	const params: Record<string, string> = {
		programId: filters.programId,
		transactionIds: filters.transactionIds,
	};
	if (filters.orgUnitIds) params.orgUnitIds = filters.orgUnitIds;

	return instanceBo
		.get("/reports/disbursed-incentives/visits/bulk", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

/**
 * SIMS-8178 — every exported disbursement's linked visits.
 *
 * Chunked because the ids travel in the query string. Sequential rather than
 * parallel: an export of 5000 rows is 50 requests, and firing those at once
 * would give the API a burst it has no reason to absorb for a download the user
 * is already waiting on.
 *
 * A failed chunk rejects the whole call. Half a Sheet 2 is worse than no Sheet
 * 2 — a finance reader cannot tell a disbursement with no visits from one whose
 * visits failed to load, and SIMS-8138 would render the difference as a
 * residual that does not exist.
 */
const fetchAllDisbursementVisits = async (
	filters: { programId: string; orgUnitIds?: string },
	transactionIds: string[],
): Promise<DisbursementVisitsBulkResponse> => {
	const ids = transactionIds.filter(Boolean);
	const merged: DisbursementVisitsBulkResponse = {
		byTransaction: {},
		currencySymbol: "",
		labels: { singular: "", plural: "", beneficiaryHeading: "" },
	};
	if (ids.length === 0) return merged;

	for (let i = 0; i < ids.length; i += VISITS_BULK_CHUNK) {
		const chunk = ids.slice(i, i + VISITS_BULK_CHUNK);
		const res = await getDisbursementVisitsBulk({
			...filters,
			transactionIds: chunk.join(","),
		});
		Object.assign(merged.byTransaction, res.byTransaction ?? {});
		// Account settings, identical across chunks — keep the first non-empty.
		if (!merged.currencySymbol && res.currencySymbol) {
			merged.currencySymbol = res.currencySymbol;
		}
		if (!merged.labels.beneficiaryHeading && res.labels) {
			merged.labels = res.labels;
		}
	}
	return merged;
};

const getDisbursedIncentives = async (
	filters: DisbursedIncentivesFilters,
): Promise<DisbursedIncentivesResponse> => {
	const params: Record<string, string | number> = {
		programId: filters.programId,
	};
	if (filters.orgUnitIds) params.orgUnitIds = filters.orgUnitIds;
	if (filters.groupLabelIds) params.groupLabelIds = filters.groupLabelIds;
	if (filters.providerId) params.providerId = filters.providerId;
	if (filters.startDate) params.startDate = filters.startDate;
	if (filters.endDate) params.endDate = filters.endDate;
	if (filters.cin) params.cin = filters.cin;
	if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
	if (filters.pageSize !== undefined) params.pageSize = filters.pageSize;
	if (filters.sortDir) params.sortDir = filters.sortDir;

	return instanceBo
		.get("/reports/disbursed-incentives", { params })
		.then((res) => res.data.body)
		.catch((err) => Promise.reject(err.response?.data ?? err));
};

/**
 * Every disbursement in the filtered set, for the SIMS-8155 export.
 *
 * AC1's "the on-screen rows" means the report, not the visible page — the same
 * way AC2's total is set-wide. Paged at the API's maximum and capped, mirroring
 * `fetchAllVisitDetail`; the cap is a backstop against an unbounded payout set,
 * and the caller is told when it bites rather than silently receiving a
 * truncated file.
 */
const fetchAllDisbursedIncentives = async (
	filters: DisbursedIncentivesFilters,
): Promise<{ rows: DisbursedIncentiveRow[]; truncated: boolean }> => {
	const PAGE_SIZE = 100;
	const EXPORT_CAP = 5000;
	let pageNumber = 0;
	const rows: DisbursedIncentiveRow[] = [];
	let total = 0;
	for (;;) {
		const res = await getDisbursedIncentives({
			...filters,
			pageNumber,
			pageSize: PAGE_SIZE,
		});
		total = res.pagination.total;
		rows.push(...res.rows);
		pageNumber += 1;
		if (
			res.rows.length === 0 ||
			rows.length >= res.pagination.total ||
			rows.length >= EXPORT_CAP
		) {
			break;
		}
	}
	return { rows, truncated: total > rows.length };
};

export const reportsService = {
	getDisbursedIncentives,
	getDisbursementVisits,
	getDisbursementVisitsBulk,
	fetchAllDisbursementVisits,
	fetchAllDisbursedIncentives,
	getEnrollmentSummary,
	getIncentiveDisbursement,
	getEducationalEngagement,
	getEventActivity,
	getEventRatings,
	getEnrollmentSummaryConfigurable,
	getEnrollmentSummaryConfigurableExcel,
	fetchAllEnrollmentSummaryConfigurable,
	getProgramActivitySummary,
	getVisitDetail,
	getVisitAnalytes,
	getVisitDetailExcel,
	fetchAllVisitDetail,
};
